import { FC, useState } from "react";  
import { UseRouteAssistant } from "../../utilities/RouteAssistant/RouteAssistant";  
import { UseUserAssistant } from "@codes-on-us/assistant";  
import classes from './signup.module.scss';

const Signup: FC = () => {  
    const { updateUser } = UseUserAssistant();  
    const { GoTo } = UseRouteAssistant();  
    
    const [formData, setFormData] = useState({  
        firstName: '',  
        lastName: '',  
        email: '',  
        phone: '',  
        officePhoneNumber: '',  
        password: '',  
        teethNumberingSystem: '',  
        postalCode: '',  
        dentalPracticeName: '',  
        address: ''  
    });  

    // State to manage validation errors  
    const [errors, setErrors] = useState<{ [key: string]: string }>({});  

    // Handle input changes  
    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {  
        const { name, value } = e.target;  
        setFormData({ ...formData, [name]: value });  
        setErrors({ ...errors, [name]: '' }); // Clear error for the field  
    };  

    // Validate form fields  
    const validate = () => {  
        const newErrors: { [key: string]: string } = {};  
        if (!formData.firstName) newErrors.firstName = "First Name is required.";  
        if (!formData.lastName) newErrors.lastName = "Last Name is required.";  
        if (!formData.email) newErrors.email = "Email is required.";  
        if (!formData.phone) {  
            newErrors.phone = "Cell phone is required.";  
        } else if (!/^(\+\d{1,3})\d{6,}$/.test(formData.phone)) {  
            newErrors.phone = "Cell phone must start with a country code (e.g. +123) followed by the number.";  
        }  
    
        if (!formData.officePhoneNumber) {  
            newErrors.officePhoneNumber = "Office phone is required.";  
        } else if (!/^(\+\d{1,3})\d{6,}$/.test(formData.officePhoneNumber)) {  
            newErrors.officePhoneNumber = "Office phone must start with a country code (e.g. +123) followed by the number.";  
        }  
    
        
        if (!formData.password) {  
            newErrors.password = "Password is required.";  
        } else if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/.test(formData.password)) {  
            newErrors.password = "Password must be at least 8 characters long, and contain at least one uppercase letter, one lowercase letter, one digit, and one special character.";  
        }  

        if (!formData.postalCode) newErrors.postalCode = "Postal Code is required.";  
        if (!formData.address) newErrors.address = "Address is required.";  
        if (!formData.teethNumberingSystem) newErrors.teethNumberingSystem = "Numbering System is required.";  
        if (!formData.dentalPracticeName) newErrors.dentalPracticeName = "Dental Practice Name is required.";  
        return newErrors;  
    };  

    // Handle form submission  
    const handleSubmit = async (e: React.FormEvent) => {  
        e.preventDefault();  
        const validationErrors = validate();  
        
        if (Object.keys(validationErrors).length > 0) {  
            setErrors(validationErrors);  
            return; // Stop submission if validation fails  
        }  

        const dataToSubmit = {  
            ...formData,  
            teethNumberingSystem: parseInt(formData.teethNumberingSystem, 10), // Convert to integer  
        };  
        
        try {  
            const response = await fetch(`${process.env.REACT_APP_BASE_URL}/Auth/Signup`, {  
                method: 'POST',  
                headers: {  
                    "Content-Type": "application/json"  
                },  
                body: JSON.stringify(dataToSubmit)  
            });  
            const result = await response.json();  

            if (response.ok) {  
                // Handling successful signup response  
                updateUser(result.value.user); // Update user state  
                GoTo.VerificationPage(); // Navigate to the verification page  
            } else {  
                setErrors({ general: result.message || "Signup failed." }); // Display error message  
            }  
        } catch (error) {  
            setErrors({ general: "An error occurred during signup." });  
        }  
    };  

    return (  
        <div className={classes.module}>  
            <div className={classes.logo}>  
                <img src="/images/logo-dark.png" title="logo" alt="logo" />  
            </div>  
            <form onSubmit={handleSubmit} className={classes.form}>  
                {errors.general && <div className={classes.error}>{errors.general}</div>}  
                <div className={classes.formGroup}>  
                    <label htmlFor="firstName" className={formData.firstName === '' ? classes.hidden : ''}>First Name</label>
                    <input   
                        type="text"   
                        id="firstName"   
                        name="firstName"
                        placeholder="First Name"
                        value={formData.firstName}   
                        onChange={handleChange}   
                    />  
                    {errors.firstName && <div className={classes.error}>{errors.firstName}</div>}  
                </div>  
                <div className={classes.formGroup}>  
                    { <label htmlFor="lastName" className={formData.lastName === '' ? classes.hidden : ''}>Last Name</label>  }
                    <input   
                        type="text"   
                        id="lastName"   
                        name="lastName"   
                        placeholder="Last Name"
                        value={formData.lastName}   
                        onChange={handleChange}   
                    />  
                    {errors.lastName && <div className={classes.error}>{errors.lastName}</div>}  
                </div>  
                <div className={classes.formGroup}>  
                    <label htmlFor="email" className={formData.email === '' ? classes.hidden : ''}>Email</label>  
                    <input   
                        type="email"   
                        id="email"   
                        name="email"   
                        placeholder="Email"
                        value={formData.email}   
                        onChange={handleChange}   
                    />  
                    {errors.email && <div className={classes.error}>{errors.email}</div>}  
                </div>  
                <div className={classes.formGroup}>  
                    <label htmlFor="phone" className={formData.phone === '' ? classes.hidden : ''}>Cell Phone</label>  
                    <input   
                        type="tel"   
                        id="phone"   
                        name="phone"   
                        placeholder="Cell Phone"
                        value={formData.phone}   
                        onChange={handleChange}   
                    />  
                    {errors.phone && <div className={classes.error}>{errors.phone}</div>}  
                </div>  
                <div className={classes.formGroup}>  
                    <label htmlFor="officePhoneNumber" className={formData.officePhoneNumber === '' ? classes.hidden : ''}>Office Phone Number</label>  
                    <input   
                        type="tel"   
                        id="officePhoneNumber"   
                        name="officePhoneNumber"   
                        placeholder="Office Phone Number"
                        value={formData.officePhoneNumber}   
                        onChange={handleChange}   
                    />  
                    {errors.officePhoneNumber && <div className={classes.error}>{errors.officePhoneNumber}</div>}  
                </div>  
                <div className={classes.formGroup}>  
                    <label htmlFor="password" className={formData.password === '' ? classes.hidden : ''}>Password</label>  
                    <input   
                        type="password"   
                        id="password"   
                        name="password"   
                        placeholder="Password"
                        value={formData.password}   
                        onChange={handleChange}   
                    />  
                    {errors.password && <div className={classes.error}>{errors.password}</div>}  
                </div>  
                <div className={classes.formGroup}>  
                    <label htmlFor="teethNumberingSystem" className={formData.teethNumberingSystem === '' ? classes.hidden : ''}>Numbering System</label>  
                    <select   
                        id="teethNumberingSystem"   
                        name="teethNumberingSystem"   
                        value={formData.teethNumberingSystem}   
                        onChange={handleChange}  
                    >  
                        <option value="">Select Your Numbering System</option>  
                        <option value="1">FDI</option>  
                        <option value="2">USA Universal</option>  
                    </select>  
                    {errors.teethNumberingSystem && <div className={classes.error}>{errors.teethNumberingSystem}</div>}  
                </div>  
                <div className={classes.formGroup}>  
                    <label htmlFor="postalCode" className={formData.postalCode === '' ? classes.hidden : ''}>Postal Code</label>  
                    <input   
                        type="text"   
                        id="postalCode"   
                        name="postalCode"   
                        placeholder="Postal Code"
                        value={formData.postalCode}   
                        onChange={handleChange}   
                    />  
                    {errors.postalCode && <div className={classes.error}>{errors.postalCode}</div>}  
                </div>  
                <div className={classes.formGroup}>  
                    <label htmlFor="dentalPracticeName"  className={formData.dentalPracticeName === '' ? classes.hidden : ''}>Dental Practice Name</label>  
                    <input   
                        type="text"   
                        id="dentalPracticeName"   
                        name="dentalPracticeName"   
                        placeholder="Dental Practice Name"
                        value={formData.dentalPracticeName}   
                        onChange={handleChange}   
                    />  
                    {errors.dentalPracticeName && <div className={classes.error}>{errors.dentalPracticeName}</div>}  
                </div>  
                <div className={classes.formGroup}>  
                    <label htmlFor="address" className={formData.address === '' ? classes.hidden : ''}>Address</label>  
                    <textarea   
                        id="address"   
                        name="address"   
                        placeholder="Address"
                        rows={3}
                        value={formData.address}   
                        onChange={handleChange}   
                    />  
                    {errors.address && <div className={classes.error}>{errors.address}</div>}  
                </div>  
                <button type="submit" className={classes.submit}>Sign up</button>  
            </form>  
            <div className={classes.footer}>  
                <small>  
                    {"Already have an account? "}  
                    <span onClick={GoTo.LoginPage} className={classes.link}>Login page</span>  
                </small>  
            </div>  
        </div>  
    );  
};  

export default Signup;  